import React from "react";
import { Typography } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";

const { Text } = Typography;

export interface TicketOption {
  id: string;
  name: string;
  distance: string;
  registration_end_date?: string;
  registration_end_time?: string;
}

interface TicketFilterProps {
  tickets: TicketOption[];
  selected?: string;
  onSelect: (ticket_id: string | undefined) => void;
  savedRangeKeys?: string[];
}

// Soft color palette — cycles across ticket cards
const TICKET_PALETTE = [
  {
    bg: "#f5f3ff",
    border: "#c4b5fd",
    selectedBg: "#ede9fe",
    selectedBorder: "#7c3aed",
    text: "#5b21b6",
    sub: "#8b5cf6",
    indicator: "#7c3aed",
  },
  {
    bg: "#eff6ff",
    border: "#93c5fd",
    selectedBg: "#dbeafe",
    selectedBorder: "#2563eb",
    text: "#1d4ed8",
    sub: "#3b82f6",
    indicator: "#2563eb",
  },
  {
    bg: "#f0fdf4",
    border: "#86efac",
    selectedBg: "#dcfce7",
    selectedBorder: "#16a34a",
    text: "#166534",
    sub: "#22c55e",
    indicator: "#16a34a",
  },
  {
    bg: "#fffbeb",
    border: "#fcd34d",
    selectedBg: "#fef3c7",
    selectedBorder: "#d97706",
    text: "#92400e",
    sub: "#f59e0b",
    indicator: "#d97706",
  },
  {
    bg: "#fff1f2",
    border: "#fda4af",
    selectedBg: "#ffe4e6",
    selectedBorder: "#e11d48",
    text: "#9f1239",
    sub: "#f43f5e",
    indicator: "#e11d48",
  },
  {
    bg: "#f0fdfa",
    border: "#5eead4",
    selectedBg: "#ccfbf1",
    selectedBorder: "#0d9488",
    text: "#115e59",
    sub: "#14b8a6",
    indicator: "#0d9488",
  },
];

const TicketFilter: React.FC<TicketFilterProps> = ({
  tickets,
  selected,
  onSelect,
  savedRangeKeys = [],
}) => {
  if (tickets.length === 0) {
    return (
      <Text type="secondary" className="text-sm italic">
        No tickets found for this distance.
      </Text>
    );
  }

  return (
    <div>
      <Text
        type="secondary"
        className="block mb-3 text-xs font-semibold uppercase tracking-wider"
      >
        Select Ticket
      </Text>
      <div className="flex flex-wrap gap-2 sm:gap-3">
        {tickets.map((t, idx) => {
          const isSelected = selected === t.id;
          const hasRange = savedRangeKeys.includes(t.id);
          const palette = TICKET_PALETTE[idx % TICKET_PALETTE.length];

          return (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(isSelected ? undefined : t.id)}
              onKeyDown={(e) => e.key === "Enter" && onSelect(isSelected ? undefined : t.id)}
              style={{
                backgroundColor: isSelected ? palette.selectedBg : palette.bg,
                borderColor: isSelected ? palette.selectedBorder : palette.border,
                borderWidth: isSelected ? 2 : 1.5,
                borderStyle: "solid",
                borderRadius: 12,
                padding: "10px 16px",
                minWidth: 110,
                cursor: "pointer",
                position: "relative",
                transition: "all 0.18s ease",
                textAlign: "center",
                userSelect: "none",
                boxShadow: isSelected
                  ? `0 2px 8px ${palette.selectedBorder}40`
                  : "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              {hasRange && (
                <CheckCircleFilled
                  style={{ position: "absolute", top: 6, right: 6, color: "#16a34a", fontSize: 11 }}
                />
              )}
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: 1.3,
                  color: palette.text,
                }}
              >
                {t.name}
              </div>
              <div
                style={{
                  fontSize: 11,
                  marginTop: 2,
                  color: isSelected ? palette.sub : "#9ca3af",
                }}
              >
                {t.distance}
              </div>
              {isSelected && (
                <div
                  style={{
                    position: "absolute",
                    bottom: -1,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 32,
                    height: 3,
                    borderRadius: 2,
                    backgroundColor: palette.indicator,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TicketFilter;
